import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Severity, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Rule-based threat hunting (part 62). A hunt is a saved, deterministic question about the audit
 * trail: field/operator/value predicates joined by AND, a window, and an optional grouping. The
 * server compiles it to parameterised SQL; this page only edits the definition and shows the
 * events a run captured. No model is involved, so the same hunt returns the same rows.
 */

type FieldKind = 'text' | 'number'

interface FieldOption {
  id: string
  label: string
  kind: FieldKind
}

interface Predicate {
  field: string
  op: string
  value: string | number | string[] | number[]
}

interface PredicateDraft {
  field: string
  op: string
  value: string
}

interface HuntTemplate {
  key: string
  name: string
  description: string
  severity: string
  window_hours: number
  group_by: string
  min_count: number
  predicates: Predicate[]
}

interface Hunt {
  id: string
  name: string
  description: string
  template_key: string
  severity: string
  window_hours: number
  group_by: string
  min_count: number
  limit_rows: number
  predicates: Predicate[]
  enabled: boolean
  created_by?: string
  created_at: string
}

interface HuntRun {
  id: string
  hunt_id: string
  status: string
  severity: string
  matched: number
  scanned: number
  error?: string
  started_at: string
  finished_at?: string
}

interface HuntFinding {
  id: string
  run_id: string
  audit_event_id: number
  occurred_at: string
  actor_type: string
  actor_id?: string
  action: string
  resource_type: string
  resource_id?: string
  severity: string
  hit_count: number
}

interface HuntInput {
  name: string
  description: string
  template_key: string
  severity: string
  window_hours: number
  group_by: string
  min_count: number
  limit_rows: number
  predicates?: Predicate[]
  enabled: boolean
}

const FIELDS: FieldOption[] = [
  { id: 'action', label: 'การกระทำ (action)', kind: 'text' },
  { id: 'resource_type', label: 'ชนิดทรัพยากร', kind: 'text' },
  { id: 'actor_type', label: 'ชนิดผู้กระทำ', kind: 'text' },
  { id: 'actor_id', label: 'รหัสผู้กระทำ', kind: 'text' },
  { id: 'resource_id', label: 'รหัสทรัพยากร', kind: 'text' },
  { id: 'request_id', label: 'รหัสคำขอ', kind: 'text' },
  { id: 'hour_of_day', label: 'ชั่วโมงของวัน (เวลาไทย)', kind: 'number' },
  { id: 'weekday', label: 'วันในสัปดาห์ (1 = จันทร์)', kind: 'number' },
]

const TEXT_OPS = [
  { id: 'eq', label: 'เท่ากับ' },
  { id: 'neq', label: 'ไม่เท่ากับ' },
  { id: 'contains', label: 'มีข้อความ' },
  { id: 'starts_with', label: 'ขึ้นต้นด้วย' },
  { id: 'in', label: 'อยู่ในรายการ (คั่นด้วยจุลภาค)' },
]

const NUMBER_OPS = [
  { id: 'eq', label: 'เท่ากับ' },
  { id: 'neq', label: 'ไม่เท่ากับ' },
  { id: 'gte', label: 'มากกว่าหรือเท่ากับ' },
  { id: 'lte', label: 'น้อยกว่าหรือเท่ากับ' },
  { id: 'between', label: 'ช่วง ปิด (low, high)' },
  { id: 'not_between', label: 'นอกช่วง (low, high)' },
]

const SEVERITIES = [
  { id: 'low', label: 'ต่ำ' },
  { id: 'medium', label: 'ปานกลาง' },
  { id: 'high', label: 'สูง' },
  { id: 'critical', label: 'วิกฤต' },
]

const GROUPS = [
  { id: '', label: 'ไม่จัดกลุ่ม' },
  { id: 'actor_id', label: 'ผู้กระทำ' },
  { id: 'action', label: 'การกระทำ' },
  { id: 'resource_type', label: 'ชนิดทรัพยากร' },
  { id: 'actor_type', label: 'ชนิดผู้กระทำ' },
]

const TEMPLATE_NAMES: Record<string, string> = {
  off_hours_admin: 'ผู้ดูแลระบบนอกเวลาทำการ',
  mass_export: 'ส่งออกข้อมูลปริมาณมาก',
  failed_logins: 'เข้าสู่ระบบล้มเหลวซ้ำ',
  privilege_change: 'เปลี่ยนแปลงสิทธิ์',
}

const templateLabel = (key: string) =>
  key === 'custom' ? 'กำหนดเอง' : (TEMPLATE_NAMES[key] ?? key)

function opsFor(field: string) {
  return FIELDS.find((f) => f.id === field)?.kind === 'number' ? NUMBER_OPS : TEXT_OPS
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function buildPredicate(draft: PredicateDraft): Predicate | null {
  const raw = draft.value.trim()
  if (!raw) return null
  const meta = FIELDS.find((f) => f.id === draft.field)
  if (!meta) return null

  if (draft.op === 'in') {
    const list = raw.split(',').map((s) => s.trim()).filter(Boolean)
    return list.length > 0 ? { field: draft.field, op: draft.op, value: list } : null
  }
  if (draft.op === 'between' || draft.op === 'not_between') {
    const parts = raw.split(',').map((s) => Number(s.trim()))
    if (parts.length !== 2 || parts.some((n) => !Number.isInteger(n))) return null
    return { field: draft.field, op: draft.op, value: parts }
  }
  if (meta.kind === 'number') {
    const parsed = Number(raw)
    if (!Number.isInteger(parsed)) return null
    return { field: draft.field, op: draft.op, value: parsed }
  }
  return { field: draft.field, op: draft.op, value: raw }
}

export function ThreatHunting() {
  const qc = useQueryClient()

  const huntsQuery = useQuery({
    queryKey: ['hunts'],
    queryFn: () => api<Hunt[]>('/v1/hunts'),
    staleTime: 20_000,
  })
  const templatesQuery = useQuery({
    queryKey: ['hunt-templates'],
    queryFn: () => api<HuntTemplate[]>('/v1/hunts/templates'),
    staleTime: 300_000,
  })

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [templateKey, setTemplateKey] = useState('off_hours_admin')
  const [severity, setSeverity] = useState('medium')
  const [windowHours, setWindowHours] = useState('72')
  const [groupBy, setGroupBy] = useState('')
  const [minCount, setMinCount] = useState('1')
  const [limitRows, setLimitRows] = useState('200')
  const [drafts, setDrafts] = useState<PredicateDraft[]>([
    { field: 'action', op: 'eq', value: '' },
  ])
  const [formError, setFormError] = useState<string | null>(null)

  const [selectedID, setSelectedID] = useState<string | null>(null)
  const [activeRun, setActiveRun] = useState<HuntRun | null>(null)

  const templates = templatesQuery.data ?? []
  const hunts = huntsQuery.data ?? []
  const selected = useMemo(
    () => hunts.find((h) => h.id === selectedID) ?? null,
    [hunts, selectedID],
  )

  const runsQuery = useQuery({
    queryKey: ['hunt-runs', selectedID],
    queryFn: () => api<HuntRun[]>(`/v1/hunts/${selectedID}/runs`),
    enabled: selectedID !== null,
    staleTime: 5_000,
  })

  const findingsQuery = useQuery({
    queryKey: ['hunt-findings', activeRun?.id],
    queryFn: () => api<HuntFinding[]>(`/v1/hunt-runs/${activeRun?.id}/findings`),
    enabled: activeRun !== null,
    staleTime: 60_000,
  })

  const save = useMutation({
    mutationFn: (input: HuntInput) => apiWrite<Hunt>('/v1/hunts', 'POST', input),
    onSuccess: (hunt) => {
      setSelectedID(hunt.id)
      setActiveRun(null)
      setFormError(null)
      setName('')
      setDescription('')
      void qc.invalidateQueries({ queryKey: ['hunts'] })
    },
  })

  const run = useMutation({
    mutationFn: (id: string) => apiWrite<HuntRun>(`/v1/hunts/${id}/run`, 'POST'),
    onSuccess: (result) => {
      setActiveRun(result)
      void qc.invalidateQueries({ queryKey: ['hunt-runs', result.hunt_id] })
    },
  })

  function applyTemplate(key: string) {
    setTemplateKey(key)
    const tpl = templates.find((t) => t.key === key)
    if (!tpl) {
      setSeverity('medium')
      setWindowHours('24')
      setGroupBy('')
      setMinCount('1')
      return
    }
    setSeverity(tpl.severity)
    setWindowHours(String(tpl.window_hours))
    setGroupBy(tpl.group_by)
    setMinCount(String(tpl.min_count))
  }

  function updateDraft(index: number, patch: Partial<PredicateDraft>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)))
  }

  function changeField(index: number, field: string) {
    const firstOp = opsFor(field)[0]?.id ?? 'eq'
    updateDraft(index, { field, op: firstOp })
  }

  function submit() {
    setFormError(null)
    if (!name.trim()) {
      setFormError('กรุณาระบุชื่อชุดค้นหา')
      return
    }

    let predicates: Predicate[] | undefined
    if (templateKey === 'custom') {
      const built = drafts.map(buildPredicate)
      if (built.some((p) => p === null)) {
        setFormError('กรุณากรอกค่าให้ครบและถูกต้องตามชนิดของฟิลด์')
        return
      }
      predicates = built as Predicate[]
      if (predicates.length === 0) {
        setFormError('ต้องมีเงื่อนไขอย่างน้อยหนึ่งข้อ')
        return
      }
    }

    const window = Number(windowHours)
    const minimum = Number(minCount)
    const limit = Number(limitRows)
    if (![window, minimum, limit].every((n) => Number.isInteger(n))) {
      setFormError('หน้าต่าง เวลา และจำนวนผลลัพธ์ต้องเป็นจำนวนเต็ม')
      return
    }
    if (groupBy === '' && minimum !== 1) {
      setFormError('หากไม่จัดกลุ่ม min_count ต้องเป็น 1')
      return
    }

    save.mutate({
      name: name.trim(),
      description: description.trim(),
      template_key: templateKey,
      severity,
      window_hours: window,
      group_by: groupBy,
      min_count: minimum,
      limit_rows: limit,
      predicates,
      enabled: true,
    })
  }

  const currentTemplate = templates.find((t) => t.key === templateKey)

  return (
    <>
      <PageHead
        title="ล่ากิจกรรมต้องสงสัย"
        sub="สร้างชุดค้นหาที่รันซ้ำได้บนร่องรอยการตรวจสอบ เงื่อนไขทุกข้อเชื่อมด้วย AND และผลลัพธ์เป็นหลักฐานที่บันทึกไว้"
        actions={
          <span className="chip">
            {number(hunts.length)} ชุดค้นหา · {number(templates.length)} แม่แบบ
          </span>
        }
      />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">สร้างชุดค้นหา</div>
          <p className="dim" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.7 }}>
            เลือกแม่แบบเพื่อเริ่มจากเงื่อนไขที่ตรวจสอบแล้ว หรือเลือก “กำหนดเอง” เพื่อประกอบเงื่อนไขเอง
            ฟิลด์และตัวดำเนินการถูกจำกัดไว้ที่ฝั่งเซิร์ฟเวอร์ ค่าถูกผูกเป็นพารามิเตอร์ทั้งหมด
          </p>

          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="hunt-name">ชื่อชุดค้นหา</label>
            <input id="hunt-name" className="input" value={name} maxLength={120}
              placeholder="เช่น ตรวจการส่งออกนอกเวลาของทีมบัญชี"
              onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="hunt-template">แม่แบบ</label>
            <select id="hunt-template" className="select" value={templateKey}
              onChange={(e) => applyTemplate(e.target.value)}>
              <option value="custom">กำหนดเอง</option>
              {templates.map((t) => (
                <option key={t.key} value={t.key}>{t.name}</option>
              ))}
            </select>
            {currentTemplate ? (
              <div className="dim" style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.7 }}>
                {currentTemplate.description}
              </div>
            ) : null}
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="hunt-description">คำอธิบาย</label>
            <textarea id="hunt-description" className="input" rows={2} value={description}
              maxLength={2000} placeholder="เหตุผลและขอบเขตของการค้นหา"
              onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: 1, minWidth: 130 }}>
              <label htmlFor="hunt-severity">ความรุนแรงฐาน</label>
              <select id="hunt-severity" className="select" value={severity}
                onChange={(e) => setSeverity(e.target.value)}>
                {SEVERITIES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div className="field" style={{ width: 110 }}>
              <label htmlFor="hunt-window">ย้อนหลัง (ชม.)</label>
              <input id="hunt-window" className="input" inputMode="numeric" value={windowHours}
                onChange={(e) => setWindowHours(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
            <div className="field" style={{ width: 110 }}>
              <label htmlFor="hunt-limit">จำนวนผล</label>
              <input id="hunt-limit" className="input" inputMode="numeric" value={limitRows}
                onChange={(e) => setLimitRows(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
          </div>

          <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: 1, minWidth: 150 }}>
              <label htmlFor="hunt-group">จัดกลุ่มตาม</label>
              <select id="hunt-group" className="select" value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}>
                {GROUPS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
            </div>
            <div className="field" style={{ width: 130 }}>
              <label htmlFor="hunt-min">อย่างน้อย (ครั้ง)</label>
              <input id="hunt-min" className="input" inputMode="numeric" value={minCount}
                onChange={(e) => setMinCount(e.target.value.replace(/[^0-9]/g, ''))} />
            </div>
          </div>

          {templateKey === 'custom' ? (
            <div style={{ marginTop: 16 }}>
              <div className="card-label">เงื่อนไข (AND ทุกข้อ)</div>
              {drafts.map((draft, index) => (
                <div key={index} className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <select className="select" style={{ flex: 1, minWidth: 140 }} value={draft.field}
                    aria-label={`ฟิลด์เงื่อนไขที่ ${index + 1}`}
                    onChange={(e) => changeField(index, e.target.value)}>
                    {FIELDS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                  <select className="select" style={{ width: 150 }} value={draft.op}
                    aria-label={`ตัวดำเนินการเงื่อนไขที่ ${index + 1}`}
                    onChange={(e) => updateDraft(index, { op: e.target.value })}>
                    {opsFor(draft.field).map((op) => (
                      <option key={op.id} value={op.id}>{op.label}</option>
                    ))}
                  </select>
                  <input className="input" style={{ flex: 1, minWidth: 140 }} value={draft.value}
                    aria-label={`ค่าเงื่อนไขที่ ${index + 1}`}
                    onChange={(e) => updateDraft(index, { value: e.target.value })} />
                  <button className="btn btn-sm btn-ghost"
                    disabled={drafts.length === 1}
                    onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== index))}>
                    ลบ
                  </button>
                </div>
              ))}
              <button className="btn btn-sm" style={{ marginTop: 10 }}
                disabled={drafts.length >= 12}
                onClick={() => setDrafts((prev) => [...prev, { field: 'action', op: 'eq', value: '' }])}>
                เพิ่มเงื่อนไข
              </button>
            </div>
          ) : null}

          {formError ? <div className="form-error" style={{ marginTop: 12 }}>{formError}</div> : null}
          {save.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>
              {errorText(save.error, 'บันทึกชุดค้นหาไม่สำเร็จ')}
            </div>
          ) : null}

          <button className="btn btn-primary" style={{ marginTop: 16 }}
            disabled={save.isPending || !name.trim()} onClick={submit}>
            {save.isPending ? 'กำลังบันทึก…' : 'บันทึกชุดค้นหา'}
          </button>
        </div>

        <div className="card card-pad">
          <div className="card-label">แม่แบบที่ให้มา</div>
          {templatesQuery.isLoading ? <Skel h={120} /> : (
            <div style={{ marginTop: 10 }}>
              {templates.map((t) => (
                <button key={t.key} className="card" style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
                  marginTop: 8, cursor: 'pointer',
                }} onClick={() => applyTemplate(t.key)}>
                  <div className="row">
                    <span style={{ fontSize: 12.5 }}>{t.name}</span>
                    <span style={{ marginLeft: 'auto' }}><Severity value={t.severity} /></span>
                  </div>
                  <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.7 }}>
                    {t.description}
                  </div>
                  <div className="dim mono" style={{ fontSize: 10.5, marginTop: 6 }}>
                    {t.window_hours} ชม. · {t.group_by || 'ไม่จัดกลุ่ม'} · min {t.min_count}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card-label" style={{ marginTop: 26, marginBottom: 10 }}>ชุดค้นหาที่บันทึกไว้</div>

      {huntsQuery.isLoading ? <Skel h={160} /> : hunts.length === 0 ? (
        <div className="card">
          <Empty title="ยังไม่มีชุดค้นหา"
            body="เลือกแม่แบบหรือกำหนดเงื่อนไขเองด้านบน แล้วบันทึกเพื่อให้ทีมตรวจสอบเรียกใช้ซ้ำได้" />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ชื่อ</th><th>แม่แบบ</th><th>ความรุนแรง</th><th>ย้อนหลัง</th>
                <th>เงื่อนไข</th><th>สร้างเมื่อ</th><th />
              </tr>
            </thead>
            <tbody>
              {hunts.map((h) => (
                <tr key={h.id} style={{ cursor: 'pointer' }}
                  onClick={() => { setSelectedID(h.id); setActiveRun(null) }}>
                  <td>{h.name}</td>
                  <td className="dim">{templateLabel(h.template_key)}</td>
                  <td><Severity value={h.severity} /></td>
                  <td className="num">{h.window_hours} ชม.</td>
                  <td className="num">{h.predicates.length}</td>
                  <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(h.created_at)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm btn-primary"
                      disabled={run.isPending && run.variables === h.id}
                      onClick={(e) => { e.stopPropagation(); setSelectedID(h.id); run.mutate(h.id) }}>
                      {run.isPending && run.variables === h.id ? 'กำลังค้นหา…' : 'เรียกใช้'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {run.isError ? (
        <div className="form-error" style={{ marginTop: 12 }}>
          {errorText(run.error, 'เรียกใช้ชุดค้นหาไม่สำเร็จ')}
        </div>
      ) : null}

      {selected ? (
        <>
          <div className="card-label" style={{ marginTop: 24, marginBottom: 10 }}>
            ประวัติการเรียกใช้ · {selected.name}
          </div>
          {runsQuery.isLoading ? <Skel h={100} /> : (runsQuery.data ?? []).length === 0 ? (
            <div className="card"><Empty title="ยังไม่เคยเรียกใช้"
              body="กด “เรียกใช้” เพื่อรันชุดค้นหานี้กับร่องรอยการตรวจสอบในช่วงเวลาที่กำหนด" /></div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>เริ่ม</th><th>สถานะ</th><th>ความรุนแรง</th>
                    <th className="num">ตรงเงื่อนไข</th><th className="num">สแกน</th><th />
                  </tr>
                </thead>
                <tbody>
                  {(runsQuery.data ?? []).map((item) => (
                    <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => setActiveRun(item)}>
                      <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(item.started_at)}</td>
                      <td>
                        <span className={`chip${item.status === 'failed' ? ' gold' : ''}`}>
                          {item.status === 'failed' ? 'ล้มเหลว' : 'สำเร็จ'}
                        </span>
                      </td>
                      <td><Severity value={item.severity} /></td>
                      <td className="num">{number(item.matched)}</td>
                      <td className="num">{number(item.scanned)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-sm btn-ghost"
                          onClick={(e) => { e.stopPropagation(); setActiveRun(item) }}>
                          ดูผล
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      {activeRun ? (
        <>
          <div className="grid-3" style={{ marginTop: 22 }}>
            <Stat label="เหตุการณ์ที่ตรงเงื่อนไข" value={number(activeRun.matched)} />
            <Stat label="เหตุการณ์ในช่วงเวลา" value={number(activeRun.scanned)}
              foot={`ย้อนหลัง ${selected?.window_hours ?? '—'} ชั่วโมง`} />
            <Stat label="ความรุนแรงของผล" value={<Severity value={activeRun.severity} />}
              foot={`สถานะ ${activeRun.status === 'failed' ? 'ล้มเหลว' : 'สำเร็จ'}`} />
          </div>

          {activeRun.error ? (
            <div className="form-error" style={{ marginTop: 12 }}>{activeRun.error}</div>
          ) : null}

          <div className="card-label" style={{ marginTop: 22, marginBottom: 10 }}>
            เหตุการณ์ที่พบ · {activeRun.id.slice(0, 8)}
          </div>

          {findingsQuery.isLoading ? <Skel h={160} /> : (findingsQuery.data ?? []).length === 0 ? (
            <div className="card">
              <Empty title="ไม่พบเหตุการณ์ที่ตรงเงื่อนไข"
                body="ช่วงเวลานี้ไม่มีร่องรอยที่ตรงกับชุดค้นหา ผลเป็นศูนย์คือคำตอบที่ถูกต้องและบันทึกไว้แล้ว" />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>เวลา</th><th>การกระทำ</th><th>ผู้กระทำ</th>
                    <th>ทรัพยากร</th><th className="num">กลุ่ม</th><th>ความรุนแรง</th>
                  </tr>
                </thead>
                <tbody>
                  {(findingsQuery.data ?? []).map((f) => (
                    <tr key={f.id}>
                      <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(f.occurred_at)}</td>
                      <td className="mono" style={{ fontSize: 11.5 }}>{f.action}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{f.actor_id || f.actor_type}</td>
                      <td className="dim" style={{ fontSize: 11.5 }}>
                        {f.resource_type}
                        {f.resource_id ? <span className="mono"> · {f.resource_id.slice(0, 8)}</span> : null}
                      </td>
                      <td className="num">{number(f.hit_count)}</td>
                      <td><Severity value={f.severity} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      <div className="dim" style={{ fontSize: 11.5, marginTop: 16, lineHeight: 1.8 }}>
        เงื่อนไขทุกข้อเชื่อมด้วย AND และประเมินในเขตเวลาไทย ระบบไม่ใช้แบบจำลองภาษาในการตัดสินผล
        ความรุนแรงจะเพิ่มขึ้นหนึ่งระดับเมื่อกลุ่มที่ใหญ่ที่สุดมีตั้งแต่ 25 เหตุการณ์ และเพิ่มอีกสองระดับเมื่อถึง 250
      </div>
    </>
  )
}
